<template>

  <div class="sidebar-container">
    <h2>{{ $t('map.sidebar.asset-info') }}</h2>

    <div class="asset-detail" >
      <div class="loader" v-if="loading">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>

      <div v-if="!loading && asset">
        <div class="asset-info">
          <div class="title">{{ $t('map.sidebar.name') }}</div>
          <div class="value">
            <a href="#" @click.prevent="$router.push({name: 'assets/id', params: {id: asset.id}})">{{ asset.name }}</a>
          </div>
        </div>

        <div class="asset-info">
          <div class="title">{{ $t('map.sidebar.device-type') }}</div>
          <div class="value">{{ asset.deviceType }}</div>
        </div>

        <div class="asset-info">
          <div class="title">{{ $t('map.sidebar.asset-type') }}</div>
          <div class="value">{{ assetType.name }}</div>
        </div>

        <div class="asset-info">
          <div class="title">{{ $t('map.sidebar.visuals') }}</div>
          <div class="value" v-if="visuals.length">
            <div :key="index" v-for="(visual, index) in visuals">
              <a href="#" @click.prevent="$router.push({name: 'visuals/id', params: {id: visual.id}})">{{ visual.name || $t('unnamed') }}</a>
            </div>
          </div>
          <div v-if="!visuals.length">
            {{ $t('map.sidebar.no-visuals') }}
          </div>
        </div>

        <div class="asset-info" v-if="asset.shvPath">
          <div class="title">{{ $t('map.sidebar.logs') }}</div>
          <div class="value">
            <a href="#" @click.prevent="$router.push({name: 'logs', query: { assets: asset.id }, params: {associatedAssets: true}})">{{ $t('map.sidebar.show-logs') }}</a>
          </div>
        </div>

        <div class="asset-info" v-if="isSingleLocation">
          <div class="title">{{ $t('map.sidebar.location') }}</div>
          <div class="value">
            <div>
              <a href="#" @click.prevent="navigate()">{{ $t('map.sidebar.navigate') }}</a>
            </div>
            <div>
              {{ selected.locations.lat }}, {{ selected.locations.lng }}
            </div>
          </div>
        </div>
      </div>

      <div v-if="!loading && !asset" class="message">
        {{ $t('map.sidebar.element-is-not-linked') }}
      </div>

      <h3 class="offset-side offset-top">{{ $t('map.sidebar.alarms') }}</h3>

      <alarms :alarms="selectedAlarms"></alarms>
    </div>

  </div>

</template>

<script>

import { mapState, mapGetters, mapActions } from 'vuex'
import Alarms from './alarms.vue'
import AssetAutocomplete from '../../assets/asset-autocomplete.vue'
import TextYesNo from '../../shared/components/text-yes-no.vue'
import { MapService } from '../services/map.service.es6'

export default {
  name: 'sidebar',

  data () {
    return {
      mapService: MapService,
      loading: false,
      asset: null,
      assetTypes: [],
      visuals: []
    }
  },

  computed: {

    ...mapState('map', [
      'selected'
    ]),

    ...mapGetters('map', [
      'alarms'
    ]),

    selectedAlarms () {
      return this.mapService.getAssetAlarms(this.asset, this.alarms)
    },

    isSingleLocation () {
      return !Array.isArray(this.selected.locations)
    },

    assetType () {
      return this.assetTypes.find(x => x.id === this.asset.assetTypeId) || {}
    }

  },

  methods: {

    ...mapActions('map', [
      'find',
    ]),

    navigate () {
      const location = this.selected.locations
      const protocol = [ 'iPhone', 'iPad', 'iPod' ].filter(platform => navigator.platform.indexOf(platform) !== -1).length ? 'maps' : 'https'

      const target = protocol + '://maps.google.com/maps?daddr=' + location.lat + ',' + location.lng

      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(position => window.open(target + '&saddr=' + position.coords.latitude + ',' + position.coords.longitude))
      } else {
        window.open(target)
      }
    },

    async refreshAsset () {
      if (!this.selected || !this.selected.assetId) {
        this.asset = null
        return
      }

      this.loading = true

      const element = await this.find(this.selected.id)

      this.asset = element.asset
      this.visuals = this.allVisuals(element)
      this.loading = false
    },

    allVisuals (element) {
      let visuals = element.visuals

      // distinct
      let result = []

      visuals.forEach(visual => {
        if (!result.filter(x => x.id === visual.id).length) {
          result.push(visual)
        }
      })

      return result
    }

  },

  async created () {
    this.refreshAsset()
    this.assetTypes = await this.$store.dispatch('getStaticItems', { model: { name: 'asset-types' } })
  },

  watch: {
    selected () {
      this.refreshAsset()
    }
  },

  components: {
    TextYesNo,
    AssetAutocomplete,
    Alarms
  },
}

</script>

<style lang="scss" scoped>

@import '../../../assets/variables';

.heading {
  padding: ($map-sidebar-padding + 5px) $map-sidebar-padding;
}

.asset-detail {

  .loader {
    text-align: center;
    margin: 30px 0;
  }

  .asset-info {
    display: flex;
    flex-direction: row;

    & > * {
      font-size: 13px !important;
      line-height: 2 !important;
      flex: 0 0 50%;
      padding: 10px 5px;

      &:first-child {
        padding-left: $map-sidebar-padding;
      }

      &:last-child {
        padding-right: $map-sidebar-padding;
      }
    }

    &:nth-child(odd) {
      background-color: #F3F3F3;
    }
  }
}

</style>
